/**
 * Online booking payment policy evaluation — shared by API and booking app.
 */

export type OnlinePaymentPolicy = 'inherit' | 'none' | 'card_on_file' | 'prepayment';

export type OnlinePaymentRequirementMode = 'none' | 'card_on_file' | 'prepayment';

export type OnlinePrepaymentType = 'full' | 'percent' | 'fixed_cents';

/** Optional online payment fields stored on each pricing template time window (JSON). */
export type PricingTimeWindowPaymentFields = {
  requirePrepayment?: boolean;
  prepaymentType?: OnlinePrepaymentType | null;
  prepaymentValue?: number | null;
};

export type OnlineBookingPaymentRuleInput = {
  id?: string;
  name?: string | null;
  isActive?: boolean;
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
  requirementMode: OnlinePaymentRequirementMode;
  prepaymentType?: OnlinePrepaymentType | null;
  prepaymentValue?: number | null;
  courseIds?: string[] | null;
  sortOrder?: number;
};

export type CustomerTypePaymentPolicyInput = {
  id: string;
  onlinePaymentPolicy?: OnlinePaymentPolicy | null;
  skipCardPromptIfOnFile?: boolean | null;
};

export type BookingPlayerSlotPaymentInput = {
  slotIndex: number;
  customerTypeId: string;
  /** Total cents for this slot (green fee + cart/extras assigned to slot). */
  amountCents: number;
};

export type SlotPaymentAction =
  | { action: 'none' }
  | { action: 'card_on_file' }
  | { action: 'prepayment'; amountCents: number; slotIndex: number };

export type OnlinePaymentRequirementBreakdownLine = {
  slotIndex: number;
  customerTypeId: string;
  action: 'card_on_file' | 'prepayment';
  amountCents: number;
};

export type OnlinePaymentRequirement =
  | {
      action: 'none';
      requiresCardOnFile: false;
      prepaymentAmountCents: 0;
      breakdown: [];
    }
  | {
      action: 'card_on_file';
      requiresCardOnFile: true;
      prepaymentAmountCents: 0;
      breakdown: OnlinePaymentRequirementBreakdownLine[];
    }
  | {
      action: 'prepayment';
      requiresCardOnFile: false;
      prepaymentAmountCents: number;
      breakdown: OnlinePaymentRequirementBreakdownLine[];
    }
  | {
      action: 'prepayment_and_card';
      requiresCardOnFile: true;
      prepaymentAmountCents: number;
      breakdown: OnlinePaymentRequirementBreakdownLine[];
    };

export function parseTimeToMinutes(time: string): number {
  const [h, m] = time.split(':').map((v) => parseInt(v, 10));
  if (!Number.isFinite(h) || !Number.isFinite(m)) return -1;
  return h * 60 + m;
}

export function coerceDaysOfWeek(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((d) => (typeof d === 'number' ? d : parseInt(String(d), 10)))
    .filter((d) => Number.isFinite(d) && d >= 0 && d <= 6);
}

export function paymentRuleMatchesTeeTime(
  rule: OnlineBookingPaymentRuleInput,
  params: { teeTimeLocal: Date; courseId: string; timeHHmm: string }
): boolean {
  if (rule.isActive === false) return false;
  if (rule.requirementMode === 'none') return false;

  const days = coerceDaysOfWeek(rule.daysOfWeek);
  if (days.length === 0) return false;
  if (!days.includes(params.teeTimeLocal.getDay())) return false;

  if (rule.courseIds && rule.courseIds.length > 0 && !rule.courseIds.includes(params.courseId)) {
    return false;
  }

  const teeMinutes = parseTimeToMinutes(params.timeHHmm);
  const start = parseTimeToMinutes(rule.startTime);
  const end = parseTimeToMinutes(rule.endTime);
  if (teeMinutes < 0 || start < 0 || end < 0) return false;
  return teeMinutes >= start && teeMinutes <= end;
}

export function resolveSlotRequirementMode(
  typePolicy: OnlinePaymentPolicy,
  clubRule: OnlineBookingPaymentRuleInput | null
): OnlinePaymentRequirementMode {
  if (typePolicy === 'none') return 'none';
  if (typePolicy === 'card_on_file' || typePolicy === 'prepayment') return typePolicy;
  if (clubRule && clubRule.requirementMode !== 'none') return clubRule.requirementMode;
  return 'none';
}

export function computePrepaymentAmountCents(
  slotAmountCents: number,
  rule: OnlineBookingPaymentRuleInput | null,
  typePolicy: OnlinePaymentPolicy
): number {
  if (typePolicy === 'prepayment') {
    return slotAmountCents;
  }
  if (!rule || rule.requirementMode !== 'prepayment') return 0;

  const prepType = rule.prepaymentType ?? 'full';
  const prepValue = rule.prepaymentValue ?? 100;

  switch (prepType) {
    case 'percent':
      return Math.round(slotAmountCents * (Math.min(100, Math.max(0, prepValue)) / 100));
    case 'fixed_cents':
      return Math.min(slotAmountCents, Math.max(0, Math.round(prepValue)));
    case 'full':
    default:
      return slotAmountCents;
  }
}

export function evaluateSlotPaymentAction(
  slot: BookingPlayerSlotPaymentInput,
  typePolicy: CustomerTypePaymentPolicyInput,
  matchingRule: OnlineBookingPaymentRuleInput | null,
  context: { teeTimeLocal: Date; courseId: string; timeHHmm: string }
): SlotPaymentAction {
  const policy = (typePolicy.onlinePaymentPolicy ?? 'inherit') as OnlinePaymentPolicy;

  const mode = resolveSlotRequirementMode(policy, matchingRule);

  if (mode === 'none') return { action: 'none' };
  if (mode === 'card_on_file') return { action: 'card_on_file' };

  const amountCents = computePrepaymentAmountCents(
    slot.amountCents,
    matchingRule,
    policy
  );
  if (amountCents <= 0) return { action: 'none' };
  return { action: 'prepayment', amountCents, slotIndex: slot.slotIndex };
}

export function aggregateBookingPaymentRequirement(
  slotActions: Array<{
    slot: BookingPlayerSlotPaymentInput;
    action: SlotPaymentAction;
  }>,
  opts?: { hasSavedCardOnFile?: boolean; skipCardPromptIfOnFileByType?: Map<string, boolean> }
): OnlinePaymentRequirement {
  const breakdown: OnlinePaymentRequirementBreakdownLine[] = [];
  let prepaymentAmountCents = 0;
  let needsCard = false;

  for (const { slot, action } of slotActions) {
    if (action.action === 'none') continue;

    if (action.action === 'card_on_file') {
      const skip =
        opts?.hasSavedCardOnFile &&
        opts.skipCardPromptIfOnFileByType?.get(slot.customerTypeId) !== false;
      if (!skip) {
        needsCard = true;
        breakdown.push({
          slotIndex: slot.slotIndex,
          customerTypeId: slot.customerTypeId,
          action: 'card_on_file',
          amountCents: 0,
        });
      }
    }

    if (action.action === 'prepayment') {
      prepaymentAmountCents += action.amountCents;
      breakdown.push({
        slotIndex: slot.slotIndex,
        customerTypeId: slot.customerTypeId,
        action: 'prepayment',
        amountCents: action.amountCents,
      });
    }
  }

  if (prepaymentAmountCents > 0 && needsCard) {
    return {
      action: 'prepayment_and_card',
      requiresCardOnFile: true,
      prepaymentAmountCents,
      breakdown,
    };
  }
  if (prepaymentAmountCents > 0) {
    return {
      action: 'prepayment',
      requiresCardOnFile: false,
      prepaymentAmountCents,
      breakdown,
    };
  }
  if (needsCard) {
    return {
      action: 'card_on_file',
      requiresCardOnFile: true,
      prepaymentAmountCents: 0,
      breakdown,
    };
  }

  return {
    action: 'none',
    requiresCardOnFile: false,
    prepaymentAmountCents: 0,
    breakdown: [],
  };
}

export function paymentRuleFromPricingTimeWindow(
  window: PricingTimeWindowPaymentFields & { startTime: string; endTime: string },
  template: { daysOfWeek: number[]; appliesToCourseIds?: string[] | null }
): OnlineBookingPaymentRuleInput | null {
  if (!window.requirePrepayment) return null;

  const days = coerceDaysOfWeek(template.daysOfWeek);
  if (days.length === 0) return null;

  const courseIds = template.appliesToCourseIds?.filter(Boolean) ?? null;

  return {
    daysOfWeek: days,
    startTime: window.startTime,
    endTime: window.endTime,
    requirementMode: 'prepayment',
    prepaymentType: window.prepaymentType ?? 'full',
    prepaymentValue: window.prepaymentValue ?? null,
    courseIds: courseIds && courseIds.length > 0 ? courseIds : null,
    isActive: true,
  };
}

export function evaluateBookingPaymentRequirement(params: {
  teeTimeLocal: Date;
  courseId: string;
  timeHHmm: string;
  rules?: OnlineBookingPaymentRuleInput[];
  /** When set, resolves the club rule per slot (e.g. from pricing template time windows). */
  getMatchingRule?: (
    slot: BookingPlayerSlotPaymentInput
  ) => OnlineBookingPaymentRuleInput | null;
  playerSlots: BookingPlayerSlotPaymentInput[];
  customerTypes: CustomerTypePaymentPolicyInput[];
  hasSavedCardOnFile?: boolean;
}): OnlinePaymentRequirement {
  const typeMap = new Map(params.customerTypes.map((t) => [t.id, t]));
  const skipMap = new Map<string, boolean>();

  for (const ct of params.customerTypes) {
    skipMap.set(ct.id, ct.skipCardPromptIfOnFile !== false);
  }

  const context = {
    teeTimeLocal: params.teeTimeLocal,
    courseId: params.courseId,
    timeHHmm: params.timeHHmm,
  };

  const sortedRules = [...(params.rules ?? [])]
    .filter((r) => r.isActive !== false)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const slotActions = params.playerSlots.map((slot) => {
    const typePolicy = typeMap.get(slot.customerTypeId) ?? {
      id: slot.customerTypeId,
      onlinePaymentPolicy: 'inherit' as const,
    };
    const matchingRule = params.getMatchingRule
      ? params.getMatchingRule(slot)
      : sortedRules.find((r) => paymentRuleMatchesTeeTime(r, context)) ?? null;
    const action = evaluateSlotPaymentAction(slot, typePolicy, matchingRule, context);
    return { slot, action };
  });

  return aggregateBookingPaymentRequirement(slotActions, {
    hasSavedCardOnFile: params.hasSavedCardOnFile,
    skipCardPromptIfOnFileByType: skipMap,
  });
}

export function onlinePaymentRequirementNeedsCheckout(
  req: OnlinePaymentRequirement
): boolean {
  return req.action !== 'none';
}
